import { useEffect, useRef, useState } from 'react'

import { ddeApi } from '@/service/ddeApi'
import { WebSocketService } from '@/service/websocketService'
import { useAuthStore } from '@/stores/useAuthStore'
import type { TeamRound } from '@/types/dde/teamRound.types'
import logger from '@/utils/_legacy/logger'

import {
  addTimestampToAction,
  areAllConnectionsReceived,
  createConnectionKey,
  createFiltersKey,
  createWebSocketUrl,
  findExistingActionIndex,
  getTeamsByPlayers,
  getTournamentRounds,
  isAuthorizationMessage,
  isTeamRoundData,
  matchesPlayerFilter,
  matchesTimeFilter
} from './teamRoundWebSocket.utils'
import type { OddsFilters } from './types'

interface UseTeamRoundWebSocketParams {
  areAllFiltersSelected: boolean
  tournamentId: string | undefined
  tournament: { rounds?: Array<{ roundNo: number }> } | null
  filters: OddsFilters
}

interface UseTeamRoundWebSocketReturn {
  teamRoundActions: TeamRound[]
  isWebSocketLoading: boolean
  rawDataRef: React.MutableRefObject<Map<TeamRound, unknown>>
}

export const useTeamRoundWebSocket = ({
  areAllFiltersSelected,
  tournamentId,
  tournament,
  filters
}: UseTeamRoundWebSocketParams): UseTeamRoundWebSocketReturn => {
  const [teamRoundActions, setTeamRoundActions] = useState<TeamRound[]>([])
  const [isWebSocketLoading, setIsWebSocketLoading] = useState(false)

  const wsConnectionsRef = useRef<Map<string, WebSocketService<TeamRound | TeamRound[]>>>(new Map())
  const collectedDataRef = useRef<TeamRound[]>([])
  const rawDataRef = useRef<Map<TeamRound, unknown>>(new Map())
  const expectedConnectionsRef = useRef<Set<string>>(new Set())
  const receivedConnectionsRef = useRef<Set<string>>(new Set())
  const filtersKeyRef = useRef<string>('')

  useEffect(() => {
    if (!areAllFiltersSelected || !tournamentId || !tournament) return

    const currentFiltersKey = createFiltersKey(tournamentId, filters)

    if (filtersKeyRef.current === currentFiltersKey && collectedDataRef.current.length > 0) {
      return
    }

    wsConnectionsRef.current.forEach((ws) => ws.disconnect())
    wsConnectionsRef.current.clear()
    collectedDataRef.current = []
    rawDataRef.current.clear()
    expectedConnectionsRef.current.clear()
    receivedConnectionsRef.current.clear()
    setTeamRoundActions([])

    filtersKeyRef.current = currentFiltersKey

    const loadTeamRoundData = async () => {
      try {
        const entryList = await ddeApi.endpoints.golf.tournaments.getEntryList(Number(tournamentId))

        const teams = getTeamsByPlayers(entryList.data, filters.players)

        if (teams.length === 0) {
          setTeamRoundActions([])
          setIsWebSocketLoading(false)
          return
        }

        const rounds = getTournamentRounds(tournament)
        setIsWebSocketLoading(true)

        const authToken = useAuthStore.getState().token
        if (!authToken) return

        teams.forEach((teamNo) => {
          rounds.forEach((roundNo) => {
            const key = createConnectionKey(teamNo, roundNo)
            expectedConnectionsRef.current.add(key)
          })
        })

        const checkAndCloseConnections = () => {
          const allConnectionsReceived = areAllConnectionsReceived(
            expectedConnectionsRef.current,
            receivedConnectionsRef.current
          )

          if (allConnectionsReceived) {
            wsConnectionsRef.current.forEach((ws) => ws.disconnect())
            wsConnectionsRef.current.clear()
            expectedConnectionsRef.current.clear()
            receivedConnectionsRef.current.clear()
            setIsWebSocketLoading(false)
          }
        }

        teams.forEach((teamNo) => {
          rounds.forEach((roundNo) => {
            const key = createConnectionKey(teamNo, roundNo)
            const url = createWebSocketUrl(tournamentId, roundNo, teamNo)

            const wsService = new WebSocketService<TeamRound | TeamRound[]>({
              url,
              heartbeatInterval: 30000
            })

            wsService.onMessage((data) => {
              if (isAuthorizationMessage(data)) {
                return
              }

              if (!isTeamRoundData(data)) {
                return
              }

              if (!receivedConnectionsRef.current.has(key)) {
                receivedConnectionsRef.current.add(key)
              }

              const transformedData: TeamRound[] = Array.isArray(data) ? data : [data]
              const messageTime = new Date()

              transformedData.forEach((action) => {
                if (!matchesPlayerFilter(action, filters.players)) {
                  return
                }

                if (
                  !matchesTimeFilter(
                    action,
                    messageTime,
                    filters.startDateTime,
                    filters.endDateTime
                  )
                ) {
                  return
                }

                const existingIndex = findExistingActionIndex(collectedDataRef.current, action)

                if (existingIndex === -1) {
                  const actionWithTimestamp = addTimestampToAction(action, messageTime)
                  collectedDataRef.current.push(actionWithTimestamp)
                  rawDataRef.current.set(actionWithTimestamp, Array.isArray(data) ? action : data)
                } else {
                  const existingAction = collectedDataRef.current[existingIndex]
                  if (!rawDataRef.current.has(existingAction)) {
                    rawDataRef.current.set(existingAction, Array.isArray(data) ? action : data)
                  }
                }
              })

              setTeamRoundActions([...collectedDataRef.current])
              checkAndCloseConnections()
            })

            wsService.onError((error) => {
              logger.error(error, `WebSocket error for team ${teamNo}, round ${roundNo}:`)
              expectedConnectionsRef.current.delete(key)
              checkAndCloseConnections()
            })

            wsService.onOpen(() => {
              if (authToken) {
                wsService.send(JSON.stringify({ authToken }))
              }
            })

            wsService.connect()
            wsConnectionsRef.current.set(key, wsService)
          })
        })
      } catch (error) {
        logger.error(error, 'Error loading team round data:')
        setIsWebSocketLoading(false)
      }
    }

    loadTeamRoundData()

    return () => {
      wsConnectionsRef.current.forEach((ws) => ws.disconnect())
      wsConnectionsRef.current.clear()
    }
  }, [
    areAllFiltersSelected,
    tournamentId,
    tournament,
    filters.marketType?.value,
    filters.players,
    filters.startDateTime,
    filters.endDateTime
  ])

  return {
    teamRoundActions,
    isWebSocketLoading,
    rawDataRef
  }
}
